import { useMediaQuery } from '@mui/material';
import { Children, cloneElement, useMemo } from 'react';

import type { Theme } from '@mui/material/styles';
import type { FC, HTMLAttributes, ReactElement } from 'react';

export interface ResponsiveStyleProps {
  children: ReactElement;
  style: {
    xs?: HTMLAttributes<HTMLElement>['style'];
    sm?: HTMLAttributes<HTMLElement>['style'];
    md?: HTMLAttributes<HTMLElement>['style'];
    lg?: HTMLAttributes<HTMLElement>['style'];
    xl?: HTMLAttributes<HTMLElement>['style'];
  };
}

const defaultStyle = {
  xs: {},
  sm: {},
  md: {},
  lg: {},
  xl: {},
};

export const ResponsiveStyle: FC<ResponsiveStyleProps> = ({
  children,
  style,
}) => {
  style = { ...defaultStyle, ...style };
  const isExtraSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up('xs')
  );
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const isMedium = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const isExtraLarge = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up('xl')
  );

  // eslint-disable-next-line max-statements
  const responsiveStyle = useMemo((): HTMLAttributes<HTMLElement>['style'] => {
    if (isExtraLarge) {
      return style.xl;
    }
    if (isLarge) {
      return style.lg;
    }
    if (isMedium) {
      return style.md;
    }
    if (isSmall) {
      return style.sm;
    }
    if (isExtraSmall) {
      return style.xs;
    }

    return style.xs;
  }, [style, isExtraSmall, isExtraLarge, isSmall, isMedium, isLarge]);

  return (
    <>
      {Children.map(children, (child, index) =>
        cloneElement(child, { key: index, style: responsiveStyle })
      )}
    </>
  );
};
